import { useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Bookmark } from 'lucide-react';
import { FeedContainer } from '../features/discovery-feed';
import type { DiscoveryListing, FeedPreferences } from '../features/discovery-feed';
import { toast } from 'sonner';

const defaultPreferences: FeedPreferences = {
  categories: ['Electronics', 'Vehicles', 'Fashion'],
  priceRange: [0, 500000],
  sortBy: 'relevance',
  conditions: [],
  excludeKeywords: [],
};

export default function DiscoveryFeedDemo(): JSX.Element {
  const [savedItems, setSavedItems] = useState<string[]>([]);

  const handleListingClick = (listing: DiscoveryListing) => {
    toast.info(`Viewing: ${listing.title}`, {
      description: `PKR ${listing.price.toLocaleString()} • ${listing.location}`,
    });
  };

  const handleSave = (listing: DiscoveryListing) => {
    setSavedItems((prev) => {
      if (prev.includes(listing.id)) {
        toast.success('Removed from saved items');
        return prev.filter((id) => id !== listing.id);
      }
      toast.success('Saved for later!', {
        icon: <Bookmark className="w-4 h-4" />,
      });
      return [...prev, listing.id];
    });
  };

  const handleShare = (listing: DiscoveryListing) => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `Check out this ${listing.title} for PKR ${listing.price.toLocaleString()}`,
        url: window.location.href,
      });
    } else {
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <div className="h-screen bg-dark-50 flex flex-col">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-b border-dark-100 px-4 py-3"
      >
        <div className="max-w-md mx-auto flex items-center justify-between">
          <h1 className="text-xl font-bold text-dark-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-500" />
            Discover
          </h1>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-sm text-dark-500">
              <Heart className="w-4 h-4" />
              <span>{savedItems.length}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Feed Container - Full Height */}
      <div className="flex-1 overflow-hidden">
        <FeedContainer
          preferences={defaultPreferences}
          onListingClick={handleListingClick}
          onSave={handleSave}
          onShare={handleShare}
        />
      </div>

      {/* Mobile Instructions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="bg-white border-t border-dark-100 px-4 py-2 text-center text-xs text-dark-400 hidden sm:block"
      >
        <p>Swipe right to like • Swipe left to pass • Tap for details</p>
      </motion.div>
    </div>
  );
}
